import { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckIcon, HStack, Pressable, VStack, useTheme } from 'native-base';

import { ClockIcon, CurrencyText, Text } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { SalesDaypart } from '../types';
import { ProgressBar } from './progress-bar';
import { VarianceBadge } from './variance-badge';
import { PulseDot } from './pulse-dot';

type DaypartCardProps = ComponentProps<typeof Pressable> & {
  variant?: 'daypart' | 'total';
  daypart: Partial<SalesDaypart>;
  isSelected?: boolean;
  displayForecastedSales?: boolean;
  dataThrough?: string | null;
  title?: string;
  caption?: string;
};

export const DaypartCard = ({
  daypart,
  isSelected,
  displayForecastedSales,
  dataThrough,
  title,
  caption,
  variant = 'daypart',
  ...pressableProps
}: DaypartCardProps) => {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  const isActive = daypart.status === 'active';
  const isComplete = daypart.status === 'completed';
  const isFuture = daypart.status === 'future';
  const isPending = daypart.status === 'pending';
  const isTotal = variant === 'total';

  return (
    <Pressable
      py={isSelected ? '5px' : 1.5}
      px={isSelected ? '11px' : 2.5}
      rounded={10}
      borderWidth={isSelected ? 2 : 1}
      borderColor={isTotal ? 'warning.400' : isSelected ? 'blueLight.600' : isActive ? 'blueLight.300' : 'gray.300'}
      bg={isTotal ? 'warning.50' : isSelected ? 'blueLight.50' : 'white'}
      style={{
        shadowColor: colors.gray[400],
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.7,
        shadowRadius: 2,
      }}
      {...pressableProps}
    >
      {/* Row 1: label */}
      <HStack alignItems="center" justifyContent="space-between">
        <HStack space={1} alignItems="center">
          <Text size="md" fontWeight={600} color={isTotal ? 'warning.800' : isFuture ? 'gray.400' : 'gray.700'}>
            {isTotal ? t('sales_tracker:total_today') : translate(daypart.label, daypart.translations)}
          </Text>
          <Text size="sm" color={isTotal ? 'warning.600' : isFuture ? 'gray.400' : 'gray.700'}>
            {`(${isTotal ? t('sales_tracker:all_dayparts') : daypart.timeStart})`}
          </Text>
        </HStack>
        {!!daypart.timeStart && isActive && <PulseDot color={colors.blue[500]} size={7} />}
      </HStack>

      {/* Row 2: actual + variance badge */}
      {(isTotal || !isFuture) && (
        <HStack space={1} alignItems="center" pt={1}>
          <CurrencyText
            size="xl"
            fontWeight={700}
            lineHeight="sm"
            color={isTotal ? 'warning.800' : isPending ? 'gray.600' : 'gray.900'}
            hideFlatZero
            value={daypart.actual}
          />
          {displayForecastedSales && !isPending && <VarianceBadge actual={daypart.actual} forecast={daypart.elapsedForecast} />}
        </HStack>
      )}

      {isFuture && displayForecastedSales && (
        <VStack pt={0.5}>
          <Text size="sm" color="gray.400">
            {t('sales_tracker:forecast')}
          </Text>
          <CurrencyText size="xl" fontWeight={700} color="gray.400" hideFlatZero value={daypart.forecast} />
        </VStack>
      )}

      {(isComplete || isPending) && (
        <VStack space={1}>
          {displayForecastedSales && (
            <Text size="sm" color="gray.500">
              {t('sales_tracker:of_forecast').split('{forecastAmount}')[0]}
              <CurrencyText size="sm" fontWeight={400} color="gray.500" hideFlatZero value={daypart.forecast} />
              {t('sales_tracker:of_total').split('{forecastAmount}')[1]} {t('sales_tracker:forecast').toLowerCase()}
            </Text>
          )}

          {isComplete && (
            <HStack space={1} alignItems="center">
              <CheckIcon size="10px" color="success.600" />
              <Text size="sm" color="success.600" fontWeight={600}>
                {t('sales_tracker:complete')}
              </Text>
            </HStack>
          )}

          {isPending && (
            <HStack space={1} alignItems="center">
              <ClockIcon size="xs" color={colors.warning[600]} />
              <Text size="sm" color="warning.600" fontWeight={600}>
                {t('sales_tracker:pending_sync')}
              </Text>
            </HStack>
          )}
        </VStack>
      )}

      {isActive && (displayForecastedSales || !!dataThrough) && (
        <VStack space={1} pt={1}>
          {displayForecastedSales && (
            <VStack space={1}>
              <Text size="sm" fontWeight={500} letterSpacing="0.05em" color={isTotal ? 'warning.800' : 'gray.500'}>
                {t('sales_tracker:forecast').toUpperCase()}
              </Text>

              <HStack alignItems="center" justifyContent="space-between">
                <HStack space={0.5} alignItems="center">
                  <Text color={isTotal ? 'warning.800' : 'gray.500'} mr={1}>
                    {t('sales_tracker:pace')}
                  </Text>
                  <CurrencyText size="sm" fontWeight={700} color="gray.900" hideFlatZero value={daypart.elapsedForecast} />
                </HStack>

                <HStack space={0.5} alignItems="center">
                  <Text color={isTotal ? 'warning.800' : 'gray.500'} mr={1}>
                    {t('sales_tracker:goal')}
                  </Text>
                  <CurrencyText size="sm" fontWeight={700} color="gray.900" hideFlatZero value={daypart.forecast} />
                </HStack>
              </HStack>
            </VStack>
          )}

          {!!dataThrough && (
            <Text size="xs" color="warning.600" pt={0.5}>
              {t('sales_tracker:data_through', { time: dataThrough })}
            </Text>
          )}

          {displayForecastedSales && (
            <ProgressBar
              bg={isTotal ? 'warning.200' : 'gray.200'}
              actual={daypart.actual ?? 0}
              elapsedForecast={daypart.elapsedForecast}
              fullForecast={daypart.forecast}
            />
          )}
        </VStack>
      )}
    </Pressable>
  );
};
